import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { OnboardingStep } from './OnboardingStep';
import { OnboardingStepProps } from './OnboardingStep';
import { onboardingService } from '@/services/onboardingService';

interface Template {
  template_name: string;
  template_category: string;
  description: string;
}

interface Templates {
  roles: Template[];
  users: Template[];
  hierarchies: Template[];
}

export const TemplateSelectionStep: React.FC<OnboardingStepProps> = ({
  data,
  onNext,
  onPrevious,
}) => {
  const { t } = useLanguage();
  const [templates, setTemplates] = useState<Templates>({
    roles: [],
    users: [],
    hierarchies: [],
  });
  const [selectedTemplates, setSelectedTemplates] = useState({
    roles: data.selectedRoleTemplates || [],
    users: data.selectedUserTemplates || [],
    hierarchies: data.selectedHierarchyTemplates || [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setIsLoading(true);
        const templatesData = await onboardingService.getIndustryTemplates(data.industryType);
        setTemplates(templatesData);
      } catch (err: any) {
        setError(err.message || 'Failed to load templates');
      } finally {
        setIsLoading(false);
      }
    };

    if (data.industryType) {
      loadTemplates();
    }
  }, [data.industryType]);

  const handleTemplateToggle = (type: 'roles' | 'users' | 'hierarchies', templateName: string) => {
    setSelectedTemplates(prev => ({
      ...prev,
      [type]: prev[type].includes(templateName)
        ? prev[type].filter(name => name !== templateName)
        : [...prev[type], templateName]
    }));
  };

  const handleSelectAll = (type: 'roles' | 'users' | 'hierarchies') => {
    const allTemplates = templates[type].map(t => t.template_name);
    setSelectedTemplates(prev => ({
      ...prev,
      [type]: allTemplates
    }));
  };

  const handleDeselectAll = (type: 'roles' | 'users' | 'hierarchies') => {
    setSelectedTemplates(prev => ({
      ...prev,
      [type]: []
    }));
  };

  const handleNext = () => {
    onNext({
      selectedRoleTemplates: selectedTemplates.roles,
      selectedUserTemplates: selectedTemplates.users,
      selectedHierarchyTemplates: selectedTemplates.hierarchies,
    });
  };

  const canGoNext = selectedTemplates.roles.length > 0 || 
                   selectedTemplates.users.length > 0 || 
                   selectedTemplates.hierarchies.length > 0;

  if (isLoading) {
    return (
      <OnboardingStep
        title={t('onboarding.templates.title')}
        description={t('onboarding.templates.description')}
        onNext={handleNext}
        onPrevious={onPrevious}
        canGoNext={false}
        showNext={true}
        showPrevious={true}
      >
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">{t('onboarding.templates.loading')}</span>
        </div>
      </OnboardingStep>
    );
  }

  if (error) {
    return (
      <OnboardingStep
        title={t('onboarding.templates.title')}
        description={t('onboarding.templates.description')}
        onNext={handleNext}
        onPrevious={onPrevious}
        canGoNext={false}
        showNext={true}
        showPrevious={true}
      >
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                {t('onboarding.templates.error.title')}
              </h3>
              <div className="mt-2 text-sm text-red-700">
                {error}
              </div>
            </div>
          </div>
        </div>
      </OnboardingStep>
    );
  }

  const renderTemplateSection = (
    type: 'roles' | 'users' | 'hierarchies',
    title: string,
    description: string,
    templates: Template[]
  ) => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          <p className="text-sm text-gray-600">{description}</p>
        </div>
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={() => handleSelectAll(type)}
            className="text-sm text-blue-600 hover:text-blue-500"
          >
            {t('onboarding.templates.selectAll')}
          </button>
          <button
            type="button"
            onClick={() => handleDeselectAll(type)}
            className="text-sm text-gray-600 hover:text-gray-500"
          >
            {t('onboarding.templates.deselectAll')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {templates.map((template) => (
          <label
            key={template.template_name}
            className={`relative rounded-lg border-2 p-4 cursor-pointer transition-all duration-200 ${
              selectedTemplates[type].includes(template.template_name)
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <input
              type="checkbox"
              checked={selectedTemplates[type].includes(template.template_name)}
              onChange={() => handleTemplateToggle(type, template.template_name)}
              className="sr-only"
            />
            <div className="flex items-start">
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900">
                  {template.template_name}
                </div>
                <div className="text-sm text-gray-500">
                  {template.template_category}
                </div>
                {template.description && (
                  <div className="mt-1 text-xs text-gray-600">
                    {template.description}
                  </div>
                )}
              </div>
              <div className="flex-shrink-0 ml-3">
                {selectedTemplates[type].includes(template.template_name) && (
                  <div className="w-5 h-5 bg-blue-600 rounded flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );

  return (
    <OnboardingStep
      title={t('onboarding.templates.title')}
      description={t('onboarding.templates.description')}
      onNext={handleNext}
      onPrevious={onPrevious}
      canGoNext={canGoNext}
      showNext={true}
      showPrevious={true}
    >
      <div className="space-y-8">
        {renderTemplateSection(
          'roles',
          t('onboarding.templates.roles.title'),
          t('onboarding.templates.roles.description'),
          templates.roles
        )}

        {renderTemplateSection(
          'users',
          t('onboarding.templates.users.title'),
          t('onboarding.templates.users.description'),
          templates.users
        )}

        {renderTemplateSection(
          'hierarchies',
          t('onboarding.templates.hierarchies.title'),
          t('onboarding.templates.hierarchies.description'),
          templates.hierarchies
        )}

        {canGoNext && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">
                  {t('onboarding.templates.ready')}
                </h3>
                <div className="mt-2 text-sm text-green-700">
                  {t('onboarding.templates.nextStep')}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </OnboardingStep>
  );
};
